import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Chip, Paper, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useApiGet, useApiSend } from '../../utils/httpClientTs';
import {
  createCheckBatch,
  getCheckBatches,
  getPendingChecksBySource,
} from '../../endpoints/financePayments';
import ServerSideTable, {
  ServerSideTableColumn,
} from '../../Components/ServerSideTable';
import type { CheckBatchSummary } from '../../types/financePayments';

const fmtMoney = (n: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

const PaymentsCheckBatchesPage: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { data, isLoading, refetch } = useApiGet(
    ['finance-payments-batches', page, rowsPerPage],
    () =>
      getCheckBatches({
        page: page + 1,
        page_size: rowsPerPage,
      }),
    { enabled: true, retry: 1 }
  );

  const { data: pendingBySource } = useApiGet(
    ['finance-payments-pending-by-source'],
    getPendingChecksBySource,
    { enabled: true, retry: 1 }
  );

  const { mutate: createBatch, isPending: creating } = useApiSend(
    () => createCheckBatch(),
    (res) => {
      void refetch();
      if (res?.batch?.id) {
        navigate(`/finance/payments/check-batches/${res.batch.id}`);
      }
    }
  );

  const rows = data?.data ?? [];
  const pagination = data?.pagination;

  const columns: ServerSideTableColumn<CheckBatchSummary>[] = [
    {
      id: 'batch_number',
      label: 'Batch',
      width: '22%',
      firstCell: true,
      sortable: false,
      render: (_v, row) => row.batch_number,
    },
    {
      id: 'status',
      label: 'Status',
      width: '12%',
      sortable: false,
      render: (_v, row) => row.status,
    },
    {
      id: 'check_count',
      label: 'Checks',
      width: '10%',
      sortable: false,
      render: (_v, row) => String(row.check_count),
    },
    {
      id: 'total_amount',
      label: 'Total',
      width: '12%',
      sortable: false,
      render: (_v, row) => fmtMoney(row.total_amount),
    },
    {
      id: 'created_at',
      label: 'Created',
      width: '18%',
      sortable: false,
      render: (_v, row) =>
        row.created_at ? new Date(row.created_at).toLocaleString() : '—',
    },
  ];

  const snap = pendingBySource;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Open a batch, add pending checks, finalize when ready to deposit, and
          print the slip as often as needed.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          disabled={creating}
          onClick={() => createBatch(undefined)}
        >
          New batch
        </Button>
      </Box>

      {snap && snap.total > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Unbatched pending checks (by source)
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Same grouping as the deposit pick list: source is the prefix before
            “:” in check_source, or the full value if there is no memo.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              mt: 1,
              alignItems: 'center',
            }}
          >
            {snap.items.map((row) => (
              <Chip
                key={row.source}
                size="small"
                label={`${row.source}: ${row.count}`}
                variant="outlined"
              />
            ))}
            <Chip
              size="small"
              color="primary"
              label={`Total pending: ${snap.total}`}
            />
          </Box>
        </Paper>
      )}

      {snap !== undefined && snap.total === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No unbatched pending checks in the queue.
        </Typography>
      )}

      <ServerSideTable<CheckBatchSummary>
        data={rows}
        columns={columns}
        loading={isLoading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={pagination?.total_count ?? 0}
        totalPages={pagination?.total_pages ?? 0}
        hasNext={pagination?.has_next ?? false}
        hasPrev={pagination?.has_prev ?? false}
        order="desc"
        orderBy="created_at"
        onPageChange={setPage}
        onRowsPerPageChange={(n) => {
          setRowsPerPage(n);
          setPage(0);
        }}
        onSort={() => {}}
        onRowClick={(row) =>
          navigate(`/finance/payments/check-batches/${row.id}`)
        }
        emptyMessage="No batches yet. Create one to start deposit prep."
      />
    </Box>
  );
};

export default PaymentsCheckBatchesPage;
